import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import styles from "./DevicePage.module.css";
import DeviceItem from "./DeviceItem";
import { Card, CardContent, List } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedDevice, setActiveDetails, resetDevice, setPagination } from "store/device/deviceSlice";
import DeviceDetails from "./DeviceDetails";
import { useNavigate, useParams } from "react-router-dom";
import { useGetDevicesQuery, useUpdateDeviceMutation } from "services/data/deviceApi";
import { HidingError } from "components/errors";
import { useQueryStale } from "rtkq/useQueryStale";
import { getDefaultQuerySearch, getManagerSearch } from "store/searchbar/selectors";
import Paginator, { useNormalizePage } from "components/paginator";
import { useGetProfilesQuery } from "services/data/profileApi";

const DEFAULT_ERROR = "Ошибка соединения с сервером";

const DevicePage = ({setActiveToolbar, defaultSearch}) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {'*': pageRoute} = useParams();
    const itemsRef = useRef([]);
    
    const [updateDevice, {error: updateDeviceError, reset: updateDeviceReset}] = useUpdateDeviceMutation();

    const changePage = (event, page) => {
        const start = (page - 1) * pagination.limit;
        dispatch(setPagination({...pagination, start: start, page: page}));
        dispatch(resetDevice());
        navigate('/devices');
    };
    
    const { selectedDevice, activeDetails, pagination } = useSelector(state => state.device);
    
    const search = useSelector((state) => getManagerSearch(state.searchbar, 'device', defaultSearch.device));

    const profsQueryArgs = getDefaultQuerySearch(defaultSearch.profile);        
    const profsQuery = useGetProfilesQuery(profsQueryArgs);
    const { data: profilesData, isError: isGetProfilesError, 
            isFetching: isGetProfilesFetching, refetch: isGetProfilesRefetch } = profsQuery;
    useQueryStale({query: profsQuery, endpointName: 'getProfiles', queryArgs: profsQueryArgs});
    
    const queryArgs = {
        ...search,
        pagination: {start: pagination.start, limit: pagination.limit}
    };

    

    const deviceQuery = useGetDevicesQuery(queryArgs);
    const { data, isError,  isFetching } = deviceQuery;
    useQueryStale({query: deviceQuery, endpointName: 'getDevices', queryArgs: queryArgs});

    useEffect( () => setActiveToolbar("device"), []);
    useNormalizePage({data, pagination, actionCreator: setPagination});
    
    useEffect( ()=> {
        const [id, details] = pageRoute.split('/'); 
        itemsRef.current = itemsRef.current.slice(0, data?.body?.items?.length);
        const item = data?.body?.items.find(el => el.id === id);
        if(item) {
            dispatch(setSelectedDevice(item));
            dispatch(setActiveDetails({value: details}));
            scrollTo(item);
        }
        else {
            dispatch(resetDevice());
            navigate('/devices');
        }
    }, [data, pageRoute, dispatch]);
                                                                            
    const scrollTo = (item) => {
      const element = itemsRef.current.find(el => el.id === item.id);
      element && element.el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inlline: 'nearest' });
    };

    


    const onItemSelect = (item, details) => { 
        if(details.value) {
            dispatch(setSelectedDevice(item));
            dispatch(setActiveDetails(details));
            navigate(`/devices/${item.id}/${details.value}`);
        }
        else {
            dispatch(resetDevice());
            navigate('/devices');
        }
    };

    const onItemEdit = (params) => {
        updateDevice({...params, currentQueryArgs: queryArgs});
    };

    const isItemSelected = (item) => item?.id===selectedDevice?.id;

    const openError = () => {  
        return !! updateDeviceError || false;
    };

    const getErrorMessage = () => { 
        if(updateDeviceError) return updateDeviceError?.data?.message ?? DEFAULT_ERROR;
        else return'Ошибка';
    };

    const hideError = () => {
        if(updateDeviceError) updateDeviceReset();
    }; 
    

    const renderEmpty = <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
                            <CardContent sx={{overflow: 'auto', display: 'flex', justifyContent: 'center'}}>
                                <div>Касс пока нет</div>
                            </CardContent>
                        </Card>;

    const renderItems = data?.body.items.map((item, index) => {
        return  <DeviceItem 
                    key={item.id}
                    ref={el => itemsRef.current[index] = {id: item.id, el: el}}
                    selected={isItemSelected(item)}
                    onSelect={onItemSelect}
                    onEdit={onItemEdit}
                    item={item}
                />;
    });

    const renderBody = useMemo(() => {
        return (data?.body?.items?.length > 0) ? <List>{renderItems}</List> : renderEmpty;
    }, [data]);


    if(isFetching) return <div>Загрузка данных...</div>;
    if(isError) return <div>Ошибка!</div>;

    return (
        <div className="page">
            <HidingError 
            open={ openError() }
            message={ getErrorMessage() }
            onClose={hideError}
            />
            <div className={styles.overlayWrapper}>
            <div className="page-content">
                <div className="left">
                <div className="pagelist">
                    <div className="pagelist-body">
                        { renderBody }
                    </div> 
                    { (data?.body?.total > pagination.limit) ?
                        <Paginator 
                            changePage={changePage}
                            total={data.body.total}
                            pageSize={pagination.limit}
                            page={pagination.page}
                        /> 
                        : null
                    }
                </div>
                </div>    
                <div className="right">
                <DeviceDetails
                    item={selectedDevice}
                    details={activeDetails}
                    profiles={profilesData?.body?.items}
                    total={data?.body?.items?.length}
                />
                </div>
            </div>
            </div> 
        </div>
    );
   
};

export default DevicePage;

